import { alpha, Button, Stack, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableRow, Typography, useTheme } from '@mui/material';

import { formatAmount, ITableHeadCustomType, TableHeadCustom, TablePaginationCustom, TableSpinner } from '@zeta-core';
import { TrendDownIcon, TrendUpIcon } from '@zeta-core/assets';
import { IClientCardItem, IFactFindEntity } from '@zeta-types';
import { useLocation, useNavigate } from 'react-router';
import { RouterLinks } from '../../../advisor-route-paths';

interface TableData {
  accountId: string;
  investmentType: string;
  accountNumber: string;
  accountType: string;
  dailyChange: number;
  trend: string;
  currentHoldingValue: number;
}
interface IDocumentTableType {
  data: {
    accounts: TableData[];
    numberOfPages: number;
    totalRecords: number;
  };
  isLoading: boolean;
  table: any;
  currentTab: string;
  isClient?: boolean;
}

const HEAD_LABEL: ITableHeadCustomType[] = [
  { label: 'Investment', maxWidth: 353, show: true },
  { label: 'Account Number', width: 353, show: true },
  // { label: 'Account Type', width: 353, show: true },
  { label: 'Value of Holding', width: 353, show: true },
  { label: 'Daily Change', width: 353, show: true },
  { label: 'Actions', width: 240, show: true },
];

const AccountTable = ({ data, isLoading, table, currentTab, isClient }: IDocumentTableType) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };

  const getFilteredAccounts = () => {
    if (currentTab === 'sole') {
      return {
        ...data,
        accounts: data?.accounts?.filter((item) => item?.accountType === 'Sole'),
      };
    }
    if (currentTab === 'joint') {
      return {
        ...data,
        accounts: data?.accounts?.filter((item) => item?.accountType === 'Joint'),
      };
    }
    return data;
  };

  const getDailyChange = (item: { dailyChange: number }) => {
    if (item?.dailyChange === 0) {
      return (
        <Stack direction="row" alignItems="center">
          {item?.dailyChange}%
        </Stack>
      );
    }
    return (
      <Stack direction="row" alignItems="center">
        {item?.dailyChange > 0 ? <TrendUpIcon /> : <TrendDownIcon />}
        {item?.dailyChange}%
      </Stack>
    );
  };

  return (
    <>
      <TableContainer sx={{ maxHeight: '40vh', overflowY: 'auto' }}>
        <Table
          role="grid"
          stickyHeader
          size={table.dense ? 'small' : 'medium'}
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottomStyle: 'dashed',
            },
          }}
        >
          <TableHeadCustom headLabel={HEAD_LABEL} />

          <TableBody>
            {isLoading && <TableSpinner colSpan={12} />}
            {!getFilteredAccounts()?.accounts?.length && !isLoading && (
              <TableCell colSpan={12}>
                <Stack alignItems="center" justifyContent="center" height="15vh">
                  <Typography variant="body2" color="text.secondary">
                    No Data
                  </Typography>
                </Stack>
              </TableCell>
            )}
            {getFilteredAccounts()?.accounts?.map((item) => {
              return (
                <TableRow key={item?.accountId}>
                  <TableCell>{item?.investmentType ?? '--'}</TableCell>
                  <TableCell>{item?.accountNumber ?? '--'}</TableCell>
                  {/* <TableCell>{item?.accountType}</TableCell> */}
                  <TableCell>{formatAmount(item?.currentHoldingValue)}</TableCell>
                  <TableCell>{item?.dailyChange >= 0 ? <>{getDailyChange(item)}</> : null}</TableCell>
                  <TableCell>
                    <Button
                      variant="soft"
                      onClick={() => {
                        const data = {
                          ...state,
                          details: item,
                        };
                        navigate(isClient ? RouterLinks?.clientPortfolioDetail : RouterLinks?.managedClientPortfolioDetail, { state: isClient ? item : data });
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationCustom
        page={table.page}
        rowsPerPage={table.rowsPerPage}
        sx={{
          borderTop: `1px dashed ${alpha(theme.palette.grey[500], 0.2)}`,
        }}
        onPageChange={table.onChangePage}
        onChangeDense={table.onChangeDense}
        count={data?.numberOfPages || 0}
        onRowsPerPageChange={table.onChangeRowsPerPage}
      />
    </>
  );
};

export default AccountTable;
